import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { LoadingBlock } from '../components/Spinner';
import { useIsAdmin } from '../lib/auth';
import { swrLoad } from '../lib/swrCache';
import type { Venue } from '../types';
import SectionTabs from '../components/SectionTabs';
import { VENUES_TABS } from '../lib/section-tabs';
import { formatCategory } from '../lib/venue-categories';
import { useStoredOrigin } from '../lib/useStoredOrigin';
import { distanceMiles } from '../lib/eventFilters';
import {
  DEFAULT_VENUE_FILTERS,
  applyVenueFilters,
  type VenueFilterState,
  venueFiltersFromParams,
  venueFiltersToParams,
} from '../lib/venueFilters';
import {
  type VenueGroupByKey,
  type VenueGroupNode,
  groupVenues,
  venueGroupingFromParam,
  venueGroupingToParam,
} from '../lib/venueGrouping';
import VenueFiltersBar from '../components/VenueFiltersBar';
import VenueEditModal from '../components/VenueEditModal';
import VenueStatusSelect from '../components/VenueStatusSelect';
import VenueSourceBadge from '../components/VenueSourceBadge';
import FavoriteButton from '../components/FavoriteButton';
import { useFavorites } from '../lib/favorites';
import { useIsAuthenticated } from '@azure/msal-react';

type ViewMode = 'cards' | 'compact';
const VIEW_STORAGE_KEY = 'mhevents:venues:viewMode';
const DEFAULT_GROUP_BY: VenueGroupByKey[] = ['neighborhood'];

function normalizeViewMode(v: string | null | undefined): ViewMode | null {
  if (v === 'cards') return 'cards';
  // Treat the legacy 'list' value (older stored prefs / URLs) as 'compact'.
  if (v === 'compact' || v === 'list') return 'compact';
  return null;
}

function loadViewMode(): ViewMode {
  try {
    return normalizeViewMode(localStorage.getItem(VIEW_STORAGE_KEY)) ?? 'compact';
  } catch {
    return 'compact';
  }
}

function saveViewMode(v: ViewMode): void {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, v);
  } catch {
    /* ignore */
  }
}

export default function VenuesPublicPage() {
  const isAdmin = useIsAdmin();
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { origin, maxDistanceMiles, setOriginState } = useStoredOrigin();
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const favorites = useFavorites();
  const isAuthed = useIsAuthenticated();
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  // Filters: seed from URL once. Origin/distance are owned by
  // useStoredOrigin and merged in only at filter time.
  const [filters, setFilters] = useState<VenueFilterState>(() =>
    venueFiltersFromParams(searchParams, DEFAULT_VENUE_FILTERS),
  );

  // Grouping + view-mode driven by URL.
  const [groupBy, setGroupByState] = useState<VenueGroupByKey[]>(() => {
    const fromUrl = venueGroupingFromParam(searchParams.get('group'));
    return searchParams.has('group') ? fromUrl : DEFAULT_GROUP_BY;
  });
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return normalizeViewMode(searchParams.get('view')) ?? loadViewMode();
  });

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Load venues
  useEffect(() => {
    let cancelled = false;
    void swrLoad<Venue[]>({
      key: 'venues:public',
      fetcher: () => api.get<Venue[]>('/api/venues/public'),
      onData: (data) => setVenues(data),
      onError: () => setVenues([]),
      isCancelled: () => cancelled,
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist view-mode preference + URL params for filters/group/view.
  useEffect(() => {
    saveViewMode(viewMode);
  }, [viewMode]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const filterParams = venueFiltersToParams(filters, DEFAULT_VENUE_FILTERS);
    for (const [k, v] of Object.entries(filterParams)) {
      if (v == null) next.delete(k);
      else next.set(k, v);
    }
    const group = venueGroupingToParam(groupBy);
    if (group) next.set('group', group);
    else next.delete('group');
    if (viewMode !== 'compact') next.set('view', viewMode);
    else next.delete('view');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, groupBy, viewMode]);

  // Origin/distance live in useStoredOrigin; merge them into the filter
  // shape only at filter time so we never need bridge effects (they
  // previously caused a render loop with the geocode fallback).
  const effectiveFilters = useMemo<VenueFilterState>(
    () => ({ ...filters, origin, maxDistanceMiles }),
    [filters, origin, maxDistanceMiles],
  );

  const visible = useMemo<Venue[]>(() => {
    if (!venues) return [];
    let list = applyVenueFilters(venues, effectiveFilters);
    if (favoritesOnly) list = list.filter((v) => favorites.venueIds.has(v.rowKey));
    return list;
  }, [venues, effectiveFilters, favoritesOnly, favorites]);

  const grouped = useMemo<VenueGroupNode[]>(
    () => groupVenues(visible, groupBy),
    [visible, groupBy],
  );

  const toggle = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const distanceLabel = (v: Venue): string | null => {
    if (!origin) return null;
    const d = distanceMiles(origin.lat, origin.lng, v.lat, v.lng);
    if (!Number.isFinite(d)) return null;
    const yards = Math.round(d * 1760);
    return `${yards.toLocaleString()} yd away`;
  };

  const renderCard = (v: Venue) => {
    const dist = distanceLabel(v);
    const cats = (v.categories?.length ? v.categories : v.category ? [v.category] : [])
      .map(formatCategory)
      .join(', ');
    return (
      <div key={v.rowKey} className="col-md-6 col-lg-4">
        <div className={`card h-100${favorites.venueIds.has(v.rowKey) ? ' mh-favorite' : ''}`}>
          <div className="card-body">
            <h2 className="h5 card-title d-flex align-items-start gap-2">
              <Link to={`/venues/${v.rowKey}`} className="flex-grow-1">{v.name}</Link>
              <FavoriteButton kind="venue" targetId={v.rowKey} targetName={v.name} />
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-link p-0 text-muted mh-event-row__edit"
                  onClick={() => setEditingVenue(v)}
                  aria-label={`Edit ${v.name}`}
                  title="Edit venue"
                >
                  <i className="bi bi-pencil-square" aria-hidden="true" />
                </button>
              )}
            </h2>
            <p className="small text-muted mb-2">
              {v.partitionKey}{cats ? ` \u00b7 ${cats}` : ''}
              {dist ? <> {'\u00b7'} <span className="text-body">{dist}</span></> : null}
            </p>
            {v.address && <p className="small mb-2">{v.address}</p>}
            <p className="small mb-2">
              <VenueSourceBadge venue={v} />
            </p>
            <p className="small mb-2">
              {v.website && <a href={v.website} target="_blank" rel="noreferrer noopener">Website</a>}
              {v.instagramHandle && <> {'\u00b7'} <a href={`https://instagram.com/${v.instagramHandle}`} target="_blank" rel="noreferrer noopener">Instagram</a></>}
              {v.facebookUrl && <> {'\u00b7'} <a href={v.facebookUrl} target="_blank" rel="noreferrer noopener">Facebook</a></>}
            </p>
            <p className="small mb-0">
              <Link to={`/venues/${v.rowKey}`}>View upcoming events {'\u2192'}</Link>
            </p>
            {isAdmin && (
              <div className="mt-2">
                <VenueStatusSelect
                  venue={v}
                  size="sm"
                  onSaved={(updated) => setVenues((prev) => prev?.map((x) => (x.rowKey === updated.rowKey ? updated : x)) ?? prev)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRow = (v: Venue) => {
    const dist = distanceLabel(v);
    const cats = (v.categories?.length ? v.categories : v.category ? [v.category] : [])
      .map(formatCategory)
      .join(', ');
    return (
      <li
        key={v.rowKey}
        className={`list-group-item d-flex flex-wrap align-items-center gap-2${favorites.venueIds.has(v.rowKey) ? ' mh-favorite' : ''}`}
      >
        <FavoriteButton kind="venue" targetId={v.rowKey} targetName={v.name} />
        <Link to={`/venues/${v.rowKey}`} className="fw-semibold flex-grow-1">{v.name}</Link>
        <span className="badge text-bg-secondary">{v.partitionKey || '\u2014'}</span>
        {cats && <span className="text-muted small">{cats}</span>}
        {dist && <span className="text-muted small">{dist}</span>}
        <span className="text-muted small d-flex gap-2 ms-2">
          {v.instagramHandle && (
            <a href={`https://instagram.com/${v.instagramHandle}`} target="_blank" rel="noreferrer noopener" aria-label={`${v.name} on Instagram`}>
              <i className="bi bi-instagram" aria-hidden="true" />
            </a>
          )}
          {v.facebookUrl && (
            <a href={v.facebookUrl} target="_blank" rel="noreferrer noopener" aria-label={`${v.name} on Facebook`}>
              <i className="bi bi-facebook" aria-hidden="true" />
            </a>
          )}
          {v.website && (
            <a href={v.website} target="_blank" rel="noreferrer noopener" aria-label={`${v.name} website`}>
              <i className="bi bi-globe" aria-hidden="true" />
            </a>
          )}
        </span>
        {isAdmin && (
          <VenueStatusSelect
            venue={v}
            size="sm"
            onSaved={(updated) => setVenues((prev) => prev?.map((x) => (x.rowKey === updated.rowKey ? updated : x)) ?? prev)}
          />
        )}
        {isAdmin && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-muted"
            onClick={() => setEditingVenue(v)}
            aria-label={`Edit ${v.name}`}
            title="Edit venue"
          >
            <i className="bi bi-pencil-square" aria-hidden="true" />
          </button>
        )}
      </li>
    );
  };

  const renderVenueList = (vs: Venue[]) => {
    if (viewMode === 'compact') {
      return <ul className="list-group mb-3">{vs.map(renderRow)}</ul>;
    }
    return <div className="row g-3 mb-3">{vs.map(renderCard)}</div>;
  };

  const renderNode = (node: VenueGroupNode, parentKey: string, depth: number) => {
    const key = `${parentKey}::${node.key}:${node.value}`;
    const isFlat = groupBy.length === 0;
    if (isFlat) {
      return <Fragment key={key}>{renderVenueList(node.venues)}</Fragment>;
    }
    const open = !collapsed.has(key);
    const indent = depth === 0 ? '' : 'ms-3';
    const headerClass = depth === 0 ? 'fs-5 fw-semibold' : 'fw-semibold';
    return (
      <div key={key} className={`mb-3 ${indent}`}>
        <button
          type="button"
          className="btn btn-link text-start p-0 text-decoration-none d-flex align-items-center gap-2 mb-2 border-0 bg-transparent"
          aria-expanded={open}
          onClick={() => toggle(key)}
        >
          <span className={`text-body ${headerClass}`}>{node.label}</span>
          <span className="text-muted small">({node.count})</span>
          <span className="text-muted" aria-hidden="true">{open ? '\u25be' : '\u25b8'}</span>
        </button>
        {open && (
          <div className="ms-2">
            {node.children.length > 0
              ? node.children.map((c) => renderNode(c, key, depth + 1))
              : renderVenueList(node.venues)}
          </div>
        )}
      </div>
    );
  };

  const totalLabel = venues
    ? `Showing ${visible.length} of ${venues.length} venue${venues.length === 1 ? '' : 's'}`
    : '';

  return (
    <SectionTabs ariaLabel="Venues sections" items={VENUES_TABS}>
      <h1>Local venues</h1>
      <p className="lead">Bars, restaurants, music, art, and community spaces we follow.</p>

      <VenueFiltersBar
        venues={venues ?? []}
        filters={filters}
        setFilters={(updater) => setFilters(updater)}
        mode="public"
        showGrouping
        groupBy={groupBy}
        setGroupBy={setGroupByState}
        originOverride={origin}
        maxDistanceMilesOverride={maxDistanceMiles}
        onOriginChange={setOriginState}
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="View mode">
          <button
            type="button"
            className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline-secondary'}`}
            aria-pressed={viewMode === 'cards'}
            onClick={() => setViewMode('cards')}
            title="Card view"
          >
            <i className="bi bi-grid-3x3-gap" aria-hidden="true" /> Cards
          </button>
          <button
            type="button"
            className={`btn ${viewMode === 'compact' ? 'btn-primary' : 'btn-outline-secondary'}`}
            aria-pressed={viewMode === 'compact'}
            onClick={() => setViewMode('compact')}
            title="Compact list"
          >
            <i className="bi bi-list-ul" aria-hidden="true" /> Compact
          </button>
        </div>
        {isAuthed && (
          <button
            type="button"
            className={`btn btn-sm ${favoritesOnly ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setFavoritesOnly((v) => !v)}
            aria-pressed={favoritesOnly}
            title="Show only favorited venues"
          >
            <span aria-hidden="true">{'\u2605'}</span> Favorites only
          </button>
        )}
        {totalLabel && (
          <span className="text-muted small ms-auto" aria-live="polite">{totalLabel}</span>
        )}
      </div>

      {venues === null && <LoadingBlock label={'Loading venues\u2026'} />}
      {venues !== null && visible.length === 0 && (
        <div className="alert alert-info">No venues match the current filter.</div>
      )}

      {grouped.map((node) => renderNode(node, 'root', 0))}

      {editingVenue && (
        <VenueEditModal
          venue={editingVenue}
          onClose={() => setEditingVenue(null)}
          onSaved={(updated) => {
            setVenues((prev) =>
              prev ? prev.map((v) => (v.rowKey === updated.rowKey ? updated : v)) : prev,
            );
            setEditingVenue(null);
          }}
        />
      )}
    </SectionTabs>
  );
}
